import { DutyType } from '~/lib/types/duty-types';
import { Customs, OptionType, Quote, QuoteRequirements } from '~/lib/types/basket-types';
import { doesQuoteSupportDdp } from '~/lib/quotes';
import { RequirementTypes } from '~/lib/types/enums';
import { CustomsKeys } from '~/lib/types/customs';
import * as Sentry from '@sentry/react';

export type DutyAvailability = {
  ddpAvailable: boolean;
  dduDisabled: boolean;
};

// Resolve a basket item's stored dutyType against the lane's current availability.
// Returns the next dutyType that should be persisted. Callers should compare against
// the current value and only write when it differs (echo-loop avoidance).
//
// IOSS is driven by the marketplace selector, not the dutyTerms ruleset — it passes
// through unchanged regardless of DDP/DDU availability.
export function resolveDutyType(current: DutyType, opts: DutyAvailability): DutyType {
  if (current === 'IOSS' || current === '') {
    return current;
  }

  if (current === 'DDP') {
    if (opts.ddpAvailable) return 'DDP';
    return opts.dduDisabled ? '' : 'DDU';
  }

  if (current === 'DDU') {
    if (!opts.dduDisabled) return 'DDU';
    return opts.ddpAvailable ? 'DDP' : '';
  }

  return current;
}

// Case-insensitive membership check for callers that already hold raw rule options
// (e.g. the buy-again path) and don't have access to useDutiesRequirements().
export function dutyTypeMatchesOptions(current: DutyType, options: OptionType[]): boolean {
  if (!current) return true;
  const needle = current.toLowerCase();
  return options.some((o) => o?.value?.toLowerCase() === needle);
}

// Pure check for whether a given dutyTerms value is in the lane's options. Mirrors
// the derivation used by useDutiesRequirements (ddpAvailable / dduDisabled) so the
// basket factory and the hook agree on what's allowed. Returns false when the
// requirements payload is missing or the rule isn't present — safe default for
// the prefill paths that gate writes on this.
export function isDutyTermOptionAvailable(
  requirements: QuoteRequirements | undefined,
  value: 'ddp' | 'ddu' | 'ioss'
): boolean {
  const dutyTermsRule = requirements?.[RequirementTypes.CUSTOMS]?.rules?.find(
    (r) => r.key === CustomsKeys.Order.DUTYTERMS
  );
  return !!dutyTermsRule?.metadata?.options?.some((o) => (o as OptionType)?.value?.toLowerCase() === value);
}

// Resolve a basket item's customs payload against the new quote during a service
// change. Today we only know `ddpAvailable` from the quote tags; the dduDisabled
// signal lives on the requirements endpoint, which isn't fetched in the service-
// switch path. So we conservatively assume DDU is allowed here. The duties module's
// reset-on-mismatch effect re-evaluates against fresh requirements when the user
// reaches the duties screen and corrects any remaining stale value.
export function resolveCustomsForQuote(customs: Customs | undefined, quote: Quote): Customs | undefined {
  if (!customs) return customs;
  const current = customs.dutyType ?? '';
  const next = resolveDutyType(current as DutyType, {
    ddpAvailable: doesQuoteSupportDdp(quote),
    dduDisabled: false
  });
  if (next === current) return customs;
  Sentry.addBreadcrumb({
    category: 'basket',
    message: 'Reset dutyType on service switch',
    level: 'info',
    data: { from: current, to: next, service: quote?.service?.slug }
  });
  return {
    ...customs,
    dutyType: next,
    ioss: next === 'IOSS' ? customs.ioss : ''
  };
}
